// Edition / white-label branding.
//
// Loaded once at startup from GET /api/branding/public (backend env is the
// single source of truth). Applies the theme via CSS custom properties so the
// same build adapts to any edition. Defaults reproduce the Sense brand, so the
// app renders correctly even before the call resolves or if it fails.
import { API_BASE_URL } from './api';

export interface BrandingTheme {
  primary: string;
  secondary: string;
  accent: string;
  gradient: string;
}

export interface Branding {
  edition: string;
  brand_name: string;
  brand_domain: string;
  logo_url: string;
  theme: BrandingTheme;
  features: string[]; // explicit list of enabled features
}

const DEFAULT_BRANDING: Branding = {
  edition: 'sense',
  brand_name: 'Sense',
  brand_domain: 'sense-ads.io',
  logo_url: '/logo.png',
  theme: {
    primary: '#FF6B4A',
    secondary: '#E94B6C',
    accent: '#8E3A9A',
    gradient: 'linear-gradient(135deg, #FF6B4A 0%, #E94B6C 50%, #8E3A9A 100%)',
  },
  features: [], // empty == "not loaded yet" -> hasFeature() is permissive
};

let _branding: Branding = DEFAULT_BRANDING;

export function getBranding(): Branding {
  return _branding;
}

/** Map edition theme onto the existing CSS variables used across the app. */
export function applyTheme(b: Branding): void {
  const root = document.documentElement;
  const t = b.theme || ({} as BrandingTheme);
  if (t.primary) root.style.setProperty('--accent-orange', t.primary);
  if (t.secondary) root.style.setProperty('--accent-red', t.secondary);
  if (t.accent) root.style.setProperty('--accent-purple', t.accent);
  if (t.gradient) root.style.setProperty('--accent-gradient', t.gradient);
  if (b.brand_name) document.title = b.brand_name;
}

/** Fetch branding from the backend and apply the theme. Falls back to Sense. */
export async function loadBranding(): Promise<Branding> {
  try {
    const res = await fetch(`${API_BASE_URL}/api/branding/public`);
    if (res.ok) {
      const data = await res.json();
      _branding = { ...DEFAULT_BRANDING, ...data };
    }
  } catch {
    // network/backend down — keep Sense defaults
  }
  applyTheme(_branding);
  applyEditionStyles(_branding);
  return _branding;
}

/**
 * Non-Sense editions ship a full design-system override as a scoped
 * stylesheet (body.edition-<id> ...). Tag the body and lazy-load the
 * edition CSS so the same markup is restyled without touching Sense.
 */
function applyEditionStyles(b: Branding): void {
  const ed = b.edition;
  if (!ed || ed === 'sense') return;
  // The edition stylesheet (e.g. dievo.css) is bundled statically and
  // scoped to body.edition-<id>; tagging the body activates it.
  document.body.classList.add(`edition-${ed}`);
}

/**
 * Square brand mark: the advertiser avatar in the Sense Max format previews,
 * and the same mark the backend ships as the bundled logo asset on launch.
 * A white-label must never put the Sense mark on its clients' ads, so each
 * edition serves its own logo512-<edition>.png from public/.
 */
export function brandMark(): string {
  const base = process.env.PUBLIC_URL || '';
  const ed = _branding.edition;
  return ed && ed !== 'sense' ? `${base}/logo512-${ed}.png` : `${base}/sense-max-logo.jpg`;
}

/**
 * True if a feature is enabled for the current edition. Before branding loads
 * (features == []) this is permissive (returns true) so a failed/slow load
 * never hides everything; after load it reflects the backend's list.
 */
export function hasFeature(name: string): boolean {
  if (!_branding.features || _branding.features.length === 0) return true;
  return _branding.features.includes(name);
}
